import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, Search, Edit, Trash2, Calendar, Users, CheckCircle,
  RefreshCw, Eye, Filter, Download, Clock, AlertCircle, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select, Textarea } from '../../components/ui/Form';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../contexts/ToastContext';
import { tasksAPI, lookupDataAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const taskSchema = z.object({
  title: z.string().min(1, 'Task title is required'),
  description: z.string().min(1, 'Description is required'),
  assigned_to: z.string().optional(), // Allow unassigned tasks
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  importance: z.enum(['normal', 'important', 'urgent']).default('normal'),
  due_date: z.string().optional(),
  status: z.enum(['created', 'assigned', 'in_progress', 'completed']).default('created'),
});

const TasksManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, taskId: null, taskTitle: '' });
  const lastQueryRef = useRef({ status: null, priority: null });

  const defaultValues = {
    title: '',
    description: '',
    assigned_to: '', // Allow empty for unassigned
    priority: 'medium',
    importance: 'normal',
    due_date: '',
    status: 'created',
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(taskSchema),
    defaultValues,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const nextQuery = { status: filterStatus, priority: filterPriority };
    if (
      lastQueryRef.current.status === nextQuery.status &&
      lastQueryRef.current.priority === nextQuery.priority
    ) {
      return;
    }
    lastQueryRef.current = nextQuery;
    loadTasks();
  }, [filterStatus, filterPriority]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadInitialData = async () => {
    try {
      const [usersData] = await Promise.all([
        lookupDataAPI.getUsers(user?.branch_id, user?.school_id)
      ]);
      
      setUsers(usersData.users || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load data', 'Please try again');
    }
  };

  const loadTasks = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      
      console.log('🌐 Loading tasks with params:', params);
      const response = await tasksAPI.getTasks(params);
      console.log('✅ Tasks API Response:', response);
      
      // Handle different response structures
      let mappedTasks = [];
      if (Array.isArray(response)) {
        mappedTasks = response;
      } else if (response?.tasks) {
        mappedTasks = response.tasks;
      } else if (response?.data) {
        mappedTasks = Array.isArray(response.data) ? response.data : [];
      }
      
      console.log('📊 Mapped tasks:', mappedTasks);
      setTasks(mappedTasks);
    } catch (error) {
      console.error('❌ Failed to load tasks:', error);
      toast.error('Failed to load tasks', error.message || 'Please try again');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      const [, usersData] = await Promise.all([
        loadTasks(),
        lookupDataAPI.getUsers(user?.branch_id, user?.school_id)
      ]);
      setUsers(usersData.users || []);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const taskData = {
        title: data.title,
        description: data.description,
        assigned_to: data.assigned_to || null, // Allow null for unassigned
        assigned_by: user.id, // Current user creating the task
        priority: data.priority,
        importance: data.importance,
        due_date: data.due_date || null,
        status: data.assigned_to ? 'assigned' : 'created', // Auto-assign status if assigned to someone
      };

      console.log('📝 Submitting task data:', taskData);

      if (editingTask) {
        await tasksAPI.updateTask(editingTask.id, taskData);
        toast.success('Task updated successfully!');
      } else {
        await tasksAPI.createTask(taskData);
        toast.success('Task created successfully!');
      }
      
      setShowForm(false);
      setEditingTask(null);
      reset();
      await loadTasks();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save task';
      toast.error('Failed to save task', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    reset({
      title: task.title,
      description: task.description,
      assigned_to: task.assigned_to || '',
      priority: task.priority,
      importance: task.importance,
      due_date: task.due_date || '',
      status: task.status,
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingTask(null);
    reset(defaultValues);
    setShowForm(true);
  };

  const handleDelete = (task) => {
    setDeleteConfirm({ show: true, taskId: task.id, taskTitle: task.title });
  };

  const confirmDelete = async () => {
    try {
      await tasksAPI.deleteTask(deleteConfirm.taskId);
      toast.success('Task deleted successfully!');
      await loadTasks();
      setDeleteConfirm({ show: false, taskId: null, taskTitle: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete task';
      toast.error('Failed to delete task', errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, taskId: null, taskTitle: '' });
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await tasksAPI.updateTask(taskId, { status: newStatus });
      toast.success('Task status updated!');
      await loadTasks();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update status';
      toast.error('Failed to update status', errorMessage);
    }
  };

  const handleView = (task) => {
    setViewingTask(task);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'assigned': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'created': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'important': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'normal': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'in_progress': return Clock;
      case 'cancelled': return AlertCircle;
      default: return Clock;
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const taskStats = {
    total: tasks.length,
    created: tasks.filter(t => t.status === 'created').length,
    assigned: tasks.filter(t => t.status === 'assigned').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    overdue: tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tasks Management</h1>
            <p className="text-gray-600 mt-2">Manage and track tasks and assignments</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button onClick={handleAddNew}>
              <Plus size={16} className="mr-2" />
              Add Task
            </Button>
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Tasks', value: taskStats.total, color: 'blue' },
          { label: 'Created', value: taskStats.created, color: 'gray' },
          { label: 'Assigned', value: taskStats.assigned, color: 'purple' },
          { label: 'In Progress', value: taskStats.inProgress, color: 'blue' },
          { label: 'Completed', value: taskStats.completed, color: 'green' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card>
              <CardContent className="p-4">
                <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <Card>
              <CardHeader>
                <CardTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Task Title</Label>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Enter task title"
                          {...field}
                          error={errors.title?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          placeholder="Enter task description"
                          {...field}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="assigned_to">Assign To</Label>
                    <Controller
                      name="assigned_to"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} error={errors.assigned_to?.message}>
                          <option value="">Select User (Optional)</option>
                          {users.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.full_name || user.email || `User ${user.id}`}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Controller
                      name="priority"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} error={errors.priority?.message}>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="importance">Importance</Label>
                    <Controller
                      name="importance"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} error={errors.importance?.message}>
                          <option value="normal">Normal</option>
                          <option value="important">Important</option>
                          <option value="urgent">Urgent</option>
                        </Select>
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="due_date">Due Date</Label>
                    <Controller
                      name="due_date"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="date"
                          {...field}
                          error={errors.due_date?.message}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} error={errors.status?.message}>
                          <option value="created">Created</option>
                          <option value="assigned">Assigned</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      className="flex-1"
                      loading={loading}
                    >
                      {editingTask ? 'Update' : 'Create'} Task
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingTask(null);
                        reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tasks List */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${showForm ? 'lg:col-span-2' : 'lg:col-span-3'}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>Tasks</CardTitle>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="sm:w-32"
                >
                  <option value="all">All Status</option>
                  <option value="created">Created</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </Select>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="sm:w-32"
                >
                  <option value="all">All Priority</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Select>
                <Button variant="outline" size="sm">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="text-gray-400 mx-auto mb-2" size={48} />
                    <p className="text-gray-500">No tasks found</p>
                  </div>
                ) : (
                  filteredTasks.map((task, index) => {
                    const assignedUser = users.find(u => u.id === task.assigned_to);
                    const StatusIcon = getStatusIcon(task.status);
                    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
                    
                    return (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                        className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-semibold text-gray-900">{task.title}</h3>
                              <Badge className={getPriorityColor(task.priority)}>
                                {task.priority}
                              </Badge>
                              <Badge className={getImportanceColor(task.importance)}>
                                {task.importance}
                              </Badge>
                              <Badge className={getStatusColor(task.status)}>
                                <StatusIcon size={12} className="mr-1" />
                                {task.status.replace('_', ' ')}
                              </Badge>
                              {isOverdue && (
                                <Badge variant="error">
                                  <AlertCircle size={12} className="mr-1" />
                                  Overdue
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Users size={14} className="mr-1" />
                                {assignedUser?.full_name || assignedUser?.email || `User ${task.assigned_to}` || 'Unassigned'}
                              </div>
                              <div className="flex items-center">
                                <Calendar size={14} className="mr-1" />
                                {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                              </div>
                              <div className="flex items-center">
                                <Clock size={14} className="mr-1" />
                                Created {new Date(task.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <Button size="sm" variant="ghost" onClick={() => handleView(task)}>
                              <Eye size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(task)}>
                              <Edit size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(task)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Task Details Modal */}
      {viewingTask && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingTask(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">{viewingTask.title}</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingTask(null)}>
                ×
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="font-medium">{viewingTask.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Assigned To</p>
                  <p className="font-medium">
                    {users.find(u => u.id === viewingTask.assigned_to)?.full_name || users.find(u => u.id === viewingTask.assigned_to)?.email || `User ${viewingTask.assigned_to}` || 'Unassigned'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Priority</p>
                  <Badge className={getPriorityColor(viewingTask.priority)}>
                    {viewingTask.priority}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusColor(viewingTask.status)}>
                    {viewingTask.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="font-medium">{new Date(viewingTask.due_date).toLocaleDateString()}</p>
                </div>
              </div>
              
              <div className="flex space-x-2 pt-4 border-t">
                {viewingTask.status !== 'completed' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      handleStatusChange(viewingTask.id, 'completed');
                      setViewingTask(null);
                    }}
                  >
                    Mark Complete
                  </Button>
                )}
                {viewingTask.status === 'pending' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      handleStatusChange(viewingTask.id, 'in_progress');
                      setViewingTask(null);
                    }}
                  >
                    Start Task
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-red-600 mr-3" size={24} />
              <h3 className="text-lg font-bold text-gray-900">Delete Task</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete the task <strong>"{deleteConfirm.taskTitle}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone and will permanently remove the task and its history.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete Task
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TasksManagement;
